import { useState } from "react";
import { awardManageClubCredit, type ClubRow, type EventRow } from "../lib/api";
import Toast from "./Toast";

/** Manage → <club> → Credits. Any owner or manager can give a racer credit
 *  at this club (e.g. a rained-out race). It's only spendable here. */
export function ManageCreditsPanel({ club, events }: { club: ClubRow; events: EventRow[] }) {
  const [email, setEmail] = useState("");
  const [dollars, setDollars] = useState("");
  const [reason, setReason] = useState("");
  const [eventId, setEventId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const amountCents = Math.round(Number(dollars) * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setError("Enter an amount greater than $0.");
      return;
    }
    setBusy(true);
    try {
      const res = await awardManageClubCredit(club.clubId, {
        email,
        amountCents,
        reason,
        relatedEventId: eventId || undefined,
      });
      setInfo(
        `Gave ${res.email} $${(amountCents / 100).toFixed(2)}. Their ${club.name} balance is now $${(
          res.newBalanceCents / 100
        ).toFixed(2)}.`,
      );
      setEmail("");
      setDollars("");
      setReason("");
      setEventId("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h2>Credits</h2>
      <p className="muted">Credit given here can only be spent on {club.name} events.</p>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}
      <form onSubmit={handleSubmit} style={{ display: "grid", gap: "0.5rem", maxWidth: 420 }}>
        <label>
          Racer's email
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label>
          Amount ($)
          <input
            type="number"
            min="0.01"
            max="1000"
            step="0.01"
            required
            value={dollars}
            onChange={(e) => setDollars(e.target.value)}
          />
        </label>
        <label>
          Reason
          <input required maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Rained out heat 3" />
        </label>
        <label>
          Event (optional)
          <select value={eventId} onChange={(e) => setEventId(e.target.value)}>
            <option value="">None</option>
            {events.map((ev) => (
              <option key={ev.eventId} value={ev.eventId}>
                {ev.title}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="btn" disabled={busy}>
          {busy ? "Giving credit…" : "Give credit"}
        </button>
      </form>
    </>
  );
}
